import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { toast } from "react-toastify";
import "./style.css";

// Regra CHA-039 do Raptor (03/11/2026) — editor de texto com arquivos no meio (contenteditable), o mesmo da
// caixa de mensagem do chat e dos campos Descrição e Testes Realizados da Nova Solicitação. Ctrl+V de print,
// clipe ou arrastar põem o arquivo onde está o cursor; Ctrl+A + Del apaga tudo, arquivos inclusive. O texto
// sai puro, com `[[arquivo:i]]` na posição de cada arquivo (o servidor grava `[[anexo:id]]`); só vai o
// arquivo que ainda está no editor.

// Nome sem acento nem caractere especial (o Portal grava em disco com o nome original). O print colado chega
// como "image.png" e ganha data e hora; repetido no mesmo editor ganha "-2", "-3".
export function prepararArquivo(uploadFile, usados) {
  const extension = uploadFile.name.includes(".") ? uploadFile.name.split(".").pop() : "";
  let baseName = uploadFile.name.replace(/\.[^/.]+$/, "");
  if (/^image$/i.test(baseName) && uploadFile.type.startsWith("image/")) {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    baseName = `print-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  }
  const limpo =
    baseName
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-zA-Z0-9\s._-]/g, "")
      .replace(/\s+/g, "_") || "arquivo";
  const comExt = (b) => (extension ? `${b}.${extension}` : b);
  let nome = comExt(limpo);
  for (let n = 2; usados.has(nome); n += 1) nome = comExt(`${limpo}-${n}`);
  return new File([uploadFile], nome, { type: uploadFile.type, lastModified: uploadFile.lastModified });
}

// Editor → texto puro com `[[arquivo:i]]` no lugar de cada arquivo (i a partir de `inicio`, para dois editores
// irem no mesmo envio), e as chaves na ordem. <br> e bloco viram "\n"; o que não é texto nem arquivo nosso
// (imagem de fora arrastada) fica de fora.
export function serializarEditor(raiz, inicio = 0) {
  const chaves = [];
  let texto = "";
  const andar = (no) => {
    for (const filho of no.childNodes) {
      if (filho.nodeType === Node.TEXT_NODE) {
        texto += filho.nodeValue;
        continue;
      }
      if (filho.nodeType !== Node.ELEMENT_NODE) continue;
      if (filho.dataset && filho.dataset.chave) {
        texto += `[[arquivo:${inicio + chaves.push(filho.dataset.chave) - 1}]]`;
        continue;
      }
      if (filho.tagName === "BR") {
        texto += "\n";
        continue;
      }
      if (/^(DIV|P)$/.test(filho.tagName) && texto && !texto.endsWith("\n")) texto += "\n";
      andar(filho);
    }
  };
  if (raiz) andar(raiz);
  return { texto: texto.replace(/ /g, " ").trim(), chaves };
}

// Só print, sem palavra nenhuma? (a Descrição exige texto).
export const temPalavras = (texto) => !!String(texto || "").replace(/\[\[(anexo|arquivo):\d+\]\]/g, "").trim();

// Arquivos (chave → { file, url }), colar e soltar no cursor, limpar e coletar para o envio. `podeAdicionar()`
// pode recusar (chamado encerrado) e avisar quem usa.
export function useEditorArquivos({ editorRef, bloqueado = false, maxArquivos, limiteBytes, podeAdicionar, porOnde = "por campo" }) {
  const arquivosRef = useRef(new Map());
  const [temConteudo, setTemConteudo] = useState(false);

  useEffect(() => {
    const mapa = arquivosRef.current;
    return () => mapa.forEach((a) => a.url && URL.revokeObjectURL(a.url));
  }, []);

  function atualizarConteudo() {
    const ed = editorRef.current;
    const tem = !!ed && (!!ed.textContent.trim() || !!ed.querySelector("[data-chave]"));
    // Apagado tudo, o navegador deixa um <br> sozinho e o placeholder (:empty) não volta.
    if (ed && !tem && ed.innerHTML && !ed.querySelector("img, [data-chave]") && !ed.textContent) ed.innerHTML = "";
    setTemConteudo(tem);
  }

  // Põe o elemento no `range` (cursor, ou ponto onde o arquivo foi solto); sem um dentro do editor, no fim.
  function inserirNoEditor(elemento, range) {
    const ed = editorRef.current;
    const sel = window.getSelection();
    let alvo = range;
    if (!alvo && sel.rangeCount && ed.contains(sel.getRangeAt(0).commonAncestorContainer)) alvo = sel.getRangeAt(0);
    if (!alvo || !ed.contains(alvo.commonAncestorContainer)) {
      alvo = document.createRange();
      alvo.selectNodeContents(ed);
      alvo.collapse(false);
    }
    alvo.deleteContents();
    alvo.insertNode(elemento);
    alvo.setStartAfter(elemento);
    alvo.collapse(true);
    sel.removeAllRanges();
    sel.addRange(alvo);
  }

  function adicionarArquivos(files, range) {
    const ed = editorRef.current;
    if (!ed || bloqueado || (podeAdicionar && !podeAdicionar())) return;
    const lista = Array.from(files || []);
    const limiteMb = Math.round(limiteBytes / (1024 * 1024));
    const grandes = lista.filter((f) => f.size > limiteBytes);
    if (grandes.length) {
      toast.error(`O arquivo "${grandes[0].name}" excede o limite de ${limiteMb}MB. Por favor, selecione um arquivo menor.`);
    }
    const aceitos = lista.filter((f) => f.size <= limiteBytes);
    const vagas = maxArquivos - ed.querySelectorAll("[data-chave]").length;
    if (aceitos.length > vagas) toast.warning(`Envie no máximo ${maxArquivos} arquivos ${porOnde}.`);
    ed.focus();
    let ponto = range;
    aceitos.slice(0, Math.max(vagas, 0)).forEach((f) => {
      const usados = new Set([...arquivosRef.current.values()].map((a) => a.file.name));
      const file = prepararArquivo(f, usados);
      const chave = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const imagem = file.type.startsWith("image/") && !file.type.includes("svg");
      const url = imagem ? URL.createObjectURL(file) : null;
      arquivosRef.current.set(chave, { file, url });
      let el;
      if (imagem) {
        el = document.createElement("img");
        el.src = url;
        el.alt = file.name;
      } else {
        el = document.createElement("span");
        el.contentEditable = "false";
        el.className = "acao_arquivo";
        el.textContent = `📎 ${file.name}`;
      }
      el.title = file.name;
      el.dataset.chave = chave;
      inserirNoEditor(el, ponto);
      ponto = null;
    });
    atualizarConteudo();
  }

  // Ctrl+V com imagem (print) ou arquivo: entra no cursor. Texto colado entra sempre como texto puro.
  function aoColar(event) {
    event.preventDefault();
    if (bloqueado) return;
    const files = Array.from(event.clipboardData?.files || []);
    if (files.length) {
      adicionarArquivos(files);
      return;
    }
    const texto = event.clipboardData?.getData("text/plain") || "";
    if (texto) document.execCommand("insertText", false, texto);
    atualizarConteudo();
  }

  function limpar() {
    arquivosRef.current.forEach((a) => a.url && URL.revokeObjectURL(a.url));
    arquivosRef.current.clear();
    if (editorRef.current) editorRef.current.innerHTML = "";
    setTemConteudo(false);
  }

  // O que vai no envio: o texto com marcadores e os arquivos que ainda estão no editor, na ordem.
  function coletar(inicio = 0) {
    const { texto, chaves } = serializarEditor(editorRef.current, inicio);
    return { texto, arquivos: chaves.map((c) => arquivosRef.current.get(c)?.file).filter(Boolean) };
  }

  return { temConteudo, atualizarConteudo, adicionarArquivos, aoColar, limpar, coletar };
}

// Campo de formulário com o editor (Nova Solicitação). Não controlado: `ref.current.coletar(inicio)` dá
// `{ texto, arquivos }`, e `ref.current.limpar()` esvazia depois do envio. Arrastar arquivo para o campo
// também põe no ponto onde foi solto.
export const CampoEditorArquivos = forwardRef(function CampoEditorArquivos(
  { placeholder = "", altura = 150, maxArquivos = 10, limiteBytes = 40 * 1024 * 1024, bloqueado = false, onChange, ariaLabel },
  ref
) {
  const editorRef = useRef(null);
  const editor = useEditorArquivos({ editorRef, bloqueado, maxArquivos, limiteBytes });
  const [arrastando, setArrastando] = useState(false);

  useImperativeHandle(ref, () => ({ coletar: editor.coletar, limpar: editor.limpar }));

  const aoMudar = () => {
    editor.atualizarConteudo();
    if (onChange) onChange(serializarEditor(editorRef.current).texto);
  };

  const temArquivoNoArraste = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

  return (
    <div
      className={`campo_editor${arrastando ? " arrastando" : ""}`}
      onDragOver={(e) => {
        if (bloqueado || !temArquivoNoArraste(e)) return;
        e.preventDefault();
        if (!arrastando) setArrastando(true);
      }}
      onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget) && setArrastando(false)}
      onDrop={(e) => {
        if (bloqueado || !temArquivoNoArraste(e)) return;
        e.preventDefault();
        setArrastando(false);
        const range = document.caretRangeFromPoint?.(e.clientX, e.clientY) || null;
        editor.adicionarArquivos(e.dataTransfer.files, range && editorRef.current?.contains(range.startContainer) ? range : null);
        aoMudar();
      }}
    >
      <div
        ref={editorRef}
        className="campo_editor_texto"
        style={{ height: altura }}
        contentEditable={!bloqueado}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        data-placeholder={placeholder}
        onPaste={(e) => {
          editor.aoColar(e);
          aoMudar();
        }}
        onInput={aoMudar}
      />
    </div>
  );
});
